import React, { useState, useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Grid,
  ButtonGroup,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Snackbar
} from "@material-ui/core";
import { Comment, Brightness2, Brightness4, Forum, LocalOffer } from "@material-ui/icons";
import Modal from "../modal/modal";
import CommentsModal from './comment-modal';
import QuestionFeedback from './question-feedback';
import TagTutorDialog from './tag-tutor-dialog';
import { CONSTANTS } from "../../constants/app-constant";
import "../exam-header/exam-header.scss";
import { applyThemeVars } from '../../constants/themes';
import { resolveInitialTheme, syncThemeBodyClass } from '../../utils/theme-preference';
import { useDispatch, useSelector } from "react-redux";
import { setPreference, resetQuesFeedback } from "../../actions";
const logo = require("../../images/logo_dark.png").default;

const ExamHeader = (props) => {
  const {
    onSelectQuestion,
    currentQuestion,
    questionData,
    onTextZoom,
    examHistoryData,
    onExamHistoryChange,
    isReview,
    readOnly = false,
    feedbackOpen,
    onFeedbackClose,
    questionMetaData,
    goToHomePage,
    isUserAcquisition,
    preferLocalMarks = false,
  } = props;
  const [currentQuestionNum, setCurrentQuestionNum] = useState(currentQuestion);
  const [marked, setMarked] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [isCommentModal, setIsCommentModal] = useState(false);
  const [quesFeedbackModal, setQuesFeedbackModal] = useState(false);
  const [currentFont, setCurrentFont] = useState('smallFont');
  const [currentTheme, setCurrentTheme] = useState('light');
  const [open, setOpen] = useState(false);
  const [feedbackErr, setFeedbackErr] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  // Student→tutor question tag (design §10.4, T3): review-mode only, students
  // only, dark unless BOTH mentorship flags are on (mirrors the backend gate).
  const tagTutorEnabled = CONSTANTS.mentorshipsEnabled && CONSTANTS.mentorshipTagsEnabled;

  const dispatch = useDispatch();

  const quesFeedbackRes = useSelector((state) => {
    return state.setQuesFeedback;
  });

  const userPreferenceData = useSelector((state) => {
    return state.setUserPreference?.preference || state.getUserPreference?.preference;
  });

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setFeedbackErr(false), 500);
  };

  useEffect(() => {
    const role = window.localStorage.getItem('role');
    setIsStudent(role === 'ROLE_STUDENT' || role === 'ROLE_TUTOR');
    setIsTutor(role === 'ROLE_TUTOR');

    // Non-logged users - apply default Light theme
    const userId = window.localStorage.getItem("userId");
    if (!userId) {
      setTheme('light');
      setCurrentTheme('light');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = window.localStorage.getItem("userId");

    if (id) {
      // S6: saved preference wins; otherwise follow the OS appearance.
      const newTheme = resolveInitialTheme(userPreferenceData);
      setCurrentTheme(newTheme);
      setTheme(newTheme);
    }
  }, [dispatch, userPreferenceData]);

  useEffect(() => {
    if (feedbackOpen && !readOnly) {
      setQuesFeedbackModal(true);
    }
  }, [feedbackOpen, readOnly]);


  useEffect(() => {
    if (quesFeedbackRes?.quesFeedback && quesFeedbackRes?.loading === false) {
      if (quesFeedbackRes?.errorMsg) {
        setFeedbackErr(quesFeedbackRes?.errorMsg);
      }
      setOpen(true)
      dispatch(resetQuesFeedback());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quesFeedbackRes]);

  useEffect(() => {
    const number = parseInt(currentQuestion);
    setCurrentQuestionNum(number);
    // After the reload that turns recording off, the marks the student made
    // come back with the answers (exam.jsx), not from the server's metadata.
    const questionId = questionData?.[currentQuestion - 1]?.questionId;
    const localMark = preferLocalMarks && questionId ? examHistoryData?.[questionId]?.marked : undefined;
    setMarked(localMark !== undefined ? localMark : questionMetaData?.[currentQuestion - 1]?.marked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, questionMetaData]);

  const onThemeChange = (changeThemeTo = null) => {
    const newTheme = changeThemeTo || (currentTheme === 'light' ? 'dark' : 'light');
    setCurrentTheme(newTheme);
    setTheme(newTheme);
    const userId = window.localStorage.getItem("userId");

    if (!isUserAcquisition && !readOnly) {
      dispatch(setPreference({ userId, body: { darkTheme: newTheme === 'dark' } }));
    }
  };

  const setTheme = (newTheme) => {
    // applyThemeVars sets the flag-selected value-set, UNSETS keys absent from it (B5:
    // in-document flag-OFF reversibility), and keeps data-skin in lockstep with the flag.
    applyThemeVars(newTheme);
    // S6: keep the round-1 design tokens' dark values in sync.
    syncThemeBodyClass(newTheme);
  }

  const handleChange = (event) => {
    const val = event.target.checked;
    setMarked(val);
    const questionId = questionData[currentQuestion - 1].questionId;

    if (!examHistoryData[questionId]) {
      examHistoryData[questionId] = {};
    }

    examHistoryData[questionId].marked = val;
    questionData[currentQuestion - 1].marked = val;
    questionMetaData[currentQuestion - 1].marked = val;

    onExamHistoryChange(examHistoryData, "marked", questionData);
  };

  const goToPreviousPage = () => {
    const number = currentQuestionNum - 1;
    setCurrentQuestionNum(number);
    onSelectQuestion(number);
  };
  const toToNextPage = () => {
    const number = currentQuestionNum + 1;
    setCurrentQuestionNum(number);
    onSelectQuestion(number);
  };

  const changeFont = (className) => {
    onTextZoom(className);
    setCurrentFont(className);
  };

  const onModalStateChange = (state) => {
    setIsCommentModal(state);
  };

  const goToHome = () => {
    goToHomePage();
  };

  const onQuesFeedbackStateChange = (state) => {
    setQuesFeedbackModal(state);
    if (!state) {
      onFeedbackClose(false);
    }
  };

  const GreenCheckbox = withStyles({
    root: {
      color: "#fff",
    },
  })((props) => <Checkbox color="default" {...props} />);

  return (
    <header className="header-container">
      <Snackbar open={open} onClose={handleClose} autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <>
          {!feedbackErr &&
            <Box className="success-alert">
              Feedback has been sent successfully. Thank you...
            </Box>
          }
          {feedbackErr &&
            <Box className="success-warn">
              {feedbackErr}
            </Box>
          }
        </>
      </Snackbar>

      <Grid container alignItems="center">
        <Grid container item xs={3} sm={3} direction="row" alignItems="center">
          <Grid item xs={12} sm={7} md={6} lg={4} className="blockContainer">
            <div>
              Item: {currentQuestion} of {questionData.length}
            </div>
            <div>Block: 1 of 1</div>
          </Grid>
          {!isUserAcquisition && !isReview &&
            <Grid item xs={12} sm={5} md={6} lg={8} className="mark-cont">
              <FormControlLabel
                control={
                  <GreenCheckbox
                    checked={marked}
                    onChange={handleChange}
                    name="checkedG"
                  />
                }
                label="Mark"
              />
            </Grid>
          }
        </Grid>
        <Grid item xs={5} sm={4} container justifyContent="center" className="next-btn-wrap">
          <img alt="logo" src={logo} width='60' height='50' className="med-logo1"></img>
          <Box component="div">
            <Button
              disabled={currentQuestionNum === 1}
              className="nav-btn prev-btn"
              onClick={goToPreviousPage}
            >
              previous
            </Button>
            <Button
              disabled={currentQuestionNum === questionData.length}
              className="nav-btn next-btn"
              onClick={toToNextPage}
            >
              next
            </Button>
          </Box>
        </Grid>
        <Grid item xs={4} sm={5} container justifyContent="flex-end" className="font-btn-wrap">
          <Box>
            {!readOnly && isReview && isStudent && !isTutor &&
              <IconButton
                className="nextBtn"
                onClick={() => onQuesFeedbackStateChange(true)}
                title="Question Feedback"
              >
                <Forum />
              </IconButton>
            }

            {!readOnly && tagTutorEnabled && isReview && isStudent && !isTutor &&
              <IconButton
                className="nextBtn"
                onClick={() => setTagDialogOpen(true)}
                title="Tag your tutor"
                data-testid="tag-tutor-open"
              >
                <LocalOffer />
              </IconButton>
            }

            {!readOnly && !isStudent && !isUserAcquisition &&
              <IconButton
                className="nextBtn"
                onClick={() => onModalStateChange(true)}
              >
                <Comment />
                Comment
              </IconButton>
            }

            <IconButton className="nextBtn marg-rt" onClick={() => onThemeChange()} title="Theme">
              {currentTheme === 'light' && <Brightness2 />}
              {currentTheme !== 'light' && <Brightness4 />}
            </IconButton>
          </Box>

          <Box component="div" m={1}>
            <ButtonGroup color="primary" size="small" className="fontContainer">
              <Button
                className={`smallFont fontBtn ${currentFont === 'smallFont' ? 'active-font-btn' : null}`}
                onClick={() => changeFont("smallFont")}
              >
                A
              </Button>
              <Button
                className={`mediumFont fontBtn ${currentFont === 'mediumFont' ? 'active-font-btn' : null}`}
                onClick={() => changeFont("mediumFont")}
              >
                A
              </Button>
              <Button
                className={`largeFont fontBtn ${currentFont === 'largeFont' ? 'active-font-btn' : null}`}
                onClick={() => changeFont("largeFont")}
              >
                A
              </Button>
            </ButtonGroup>
            <Box component="div" className="mediumFont">
              Text Zoom
            </Box>
          </Box>
          <Box>
            <img alt="logo" src={logo} width='60' height='50' className="med-logo go-to-home" onClick={goToHome}></img>
          </Box>
        </Grid>
      </Grid>
      {!readOnly && isCommentModal && (
        <Modal modalBody={
          <CommentsModal
            questionData={questionData}
            currentQuestion={currentQuestion}
            changeCommentModalStatus={onModalStateChange} />}></Modal>
      )}
      {!readOnly && quesFeedbackModal && (
        <Modal clsNm={`ques-feedbk-modal ${isTutor ? ' tutor-mode' : ''}`} modalBody={
          <QuestionFeedback
            className="ques-feedbk-modal"
            questionData={questionData}
            currentQuestion={currentQuestion}
            changeQuesFeedbackStatus={onQuesFeedbackStateChange} />}></Modal>
      )}
      {!readOnly && tagTutorEnabled && (
        <TagTutorDialog
          open={tagDialogOpen}
          onClose={() => setTagDialogOpen(false)}
          questionId={questionData?.[currentQuestion - 1]?.questionId}
        />
      )}
    </header>
  );
};

export default ExamHeader;
